import type {
  ProviderCapability,
  ProviderKind,
  ProviderProfile,
  TransportMode,
} from "./provider-profile.js";

export type ProviderApiFamily =
  | "openai-chat-compatible"
  | "anthropic-messages"
  | "google-generative"
  | "openai-images"
  | "gemini-images"
  | "google-imagen"
  | "comfy"
  | "stable-diffusion-webui"
  | "platform-gateway";

export interface ProviderCatalogEntry {
  id: string;
  label: string;
  kind: ProviderKind;
  transport: TransportMode;
  apiFamily: ProviderApiFamily;
  capabilities: ProviderCapability[];
  baseUrl?: string;
  baseUrlRequired?: boolean;
  defaultModel?: string;
  knownModels?: string[];
  requiresApiKey: boolean;
  docsUrl?: string;
  extraHeaders?: Record<string, string>;
  imageProvider?: "openai" | "gemini" | "imagen" | "comfy" | "stable-diffusion-webui";
}

export const PROVIDER_CATALOG: ProviderCatalogEntry[] = [
  entry("openai", "OpenAI", "openai", "direct", "openai-chat-compatible", ["chat", "image", "vision"], {
    defaultModel: "gpt-5.5",
    knownModels: [
      "gpt-5.5",
      "gpt-5.5-pro",
      "gpt-5.4",
      "gpt-5.4-pro",
      "gpt-5.4-mini",
      "gpt-5.4-nano",
      "gpt-5.2",
      "gpt-5.2-pro",
      "gpt-5.1",
      "gpt-5.1-chat-latest",
      "gpt-5",
      "gpt-5-mini",
      "gpt-5-nano",
      "gpt-4.1",
      "gpt-4.1-mini",
      "gpt-4o",
      "gpt-4o-mini",
    ],
    docsUrl: "https://platform.openai.com/docs",
  }),
  entry("anthropic", "Anthropic Claude", "anthropic", "proxy", "anthropic-messages", ["chat", "vision", "reasoning"], {
    defaultModel: "claude-opus-4-8",
    knownModels: [
      "claude-fable-5",
      "claude-mythos-preview",
      "claude-opus-4-8",
      "claude-opus-4-7",
      "claude-opus-4-6",
      "claude-opus-4-5-20251101",
      "claude-sonnet-4-6",
      "claude-sonnet-4-5-20250929",
      "claude-haiku-4-5-20251001",
      "claude-haiku-4-5",
    ],
    docsUrl: "https://docs.anthropic.com/",
  }),
  entry("google-gemini", "Google Gemini", "google", "direct", "google-generative", ["chat", "image", "vision"], {
    defaultModel: "gemini-3.5-flash",
    knownModels: [
      "gemini-3.5-flash",
      "gemini-3.1-pro",
      "gemini-3.1-pro-preview",
      "gemini-3.1-pro-preview-customtools",
      "gemini-3.1-flash-lite",
      "gemini-3-flash",
      "gemini-3-flash-preview",
      "gemini-3-pro-preview",
      "gemini-flash-latest",
      "gemini-pro-latest",
      "gemini-2.5-pro",
      "gemini-2.5-flash",
      "gemini-2.5-flash-lite",
    ],
    docsUrl: "https://ai.google.dev/gemini-api/docs",
  }),
  entry("openrouter", "OpenRouter", "openrouter", "direct", "openai-chat-compatible", ["chat", "vision", "reasoning"], {
    defaultModel: "openai/gpt-5.5",
    knownModels: ["openai/gpt-5.5", "anthropic/claude-sonnet-4.6", "google/gemini-3.5-flash", "openai/gpt-4o-mini"],
    docsUrl: "https://openrouter.ai/docs",
  }),
  entry("platform", "RoQeet Platform Gateway", "platform", "gateway", "platform-gateway", ["chat", "image"], {
    defaultModel: "roqeet/default",
    requiresApiKey: false,
  }),
  entry("custom-openai-compatible", "Custom OpenAI-Compatible API", "openai-compatible", "direct", "openai-chat-compatible", ["chat", "vision"], {
    baseUrlRequired: true,
    defaultModel: "local-model",
    knownModels: ["local-model", "gpt-5.5", "gpt-4o-mini", "llama3.2", "qwen2.5"],
  }),
  entry("deepseek", "DeepSeek", "openai-compatible", "direct", "openai-chat-compatible", ["chat", "reasoning"], {
    baseUrl: "https://api.deepseek.com",
    defaultModel: "deepseek-v4-flash",
    knownModels: ["deepseek-v4-flash", "deepseek-v4-pro", "deepseek-chat", "deepseek-reasoner"],
    docsUrl: "https://api-docs.deepseek.com/",
  }),
  entry("nanogpt", "NanoGPT", "openai-compatible", "direct", "openai-chat-compatible", ["chat", "image", "vision", "reasoning"], {
    baseUrl: "https://nano-gpt.com/api/v1",
    baseUrlRequired: true,
    knownModels: ["gpt-4o-mini", "claude-3-5-sonnet", "gemini-2.0-flash"],
    docsUrl: "https://docs.nano-gpt.com/",
    imageProvider: "openai",
  }),
  entry("kimi", "Kimi / Moonshot", "openai-compatible", "direct", "openai-chat-compatible", ["chat", "vision", "reasoning"], {
    baseUrl: "https://api.moonshot.ai/v1",
    defaultModel: "moonshot-v1-8k",
    knownModels: ["moonshot-v1-8k", "moonshot-v1-32k", "moonshot-v1-128k"],
    docsUrl: "https://platform.kimi.ai/docs/api/overview",
  }),
  entry("glm", "GLM / Zhipu BigModel", "openai-compatible", "direct", "openai-chat-compatible", ["chat", "vision", "reasoning"], {
    baseUrl: "https://open.bigmodel.cn/api/paas/v4",
    defaultModel: "glm-4-flash",
    knownModels: ["glm-4-flash", "glm-4-plus", "glm-4-air"],
    docsUrl: "https://open.bigmodel.cn/dev/api",
  }),
  entry("mistral", "Mistral AI", "openai-compatible", "direct", "openai-chat-compatible", ["chat", "vision"], {
    baseUrl: "https://api.mistral.ai/v1",
    defaultModel: "mistral-small-latest",
    knownModels: ["mistral-small-latest", "mistral-medium-latest", "codestral-latest"],
    docsUrl: "https://docs.mistral.ai/",
  }),
  entry("vertex", "Google Vertex AI", "openai-compatible", "proxy", "openai-chat-compatible", ["chat", "vision"], {
    baseUrlRequired: true,
    defaultModel: "gemini-2.0-flash",
    docsUrl: "https://cloud.google.com/vertex-ai/generative-ai/docs",
  }),
  entry("nvidia-nim", "NVIDIA NIM / Nemotron", "openai-compatible", "direct", "openai-chat-compatible", ["chat", "reasoning"], {
    baseUrl: "https://integrate.api.nvidia.com/v1",
    defaultModel: "nvidia/llama-3.3-nemotron-super-49b-v1.5",
    knownModels: ["nvidia/llama-3.3-nemotron-super-49b-v1.5", "meta/llama-3.1-405b-instruct"],
    docsUrl: "https://docs.api.nvidia.com/nim/reference/llm-apis",
  }),
  entry("qwen", "Qwen / DashScope", "openai-compatible", "direct", "openai-chat-compatible", ["chat", "vision", "reasoning"], {
    baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1",
    defaultModel: "qwen-plus",
    knownModels: ["qwen-plus", "qwen-turbo", "qwen-max", "qwen-vl-plus"],
    docsUrl: "https://help.aliyun.com/zh/model-studio/compatibility-of-openai-with-dashscope",
  }),
  entry("minimax", "MiniMax", "openai-compatible", "direct", "openai-chat-compatible", ["chat", "reasoning"], {
    baseUrl: "https://api.minimax.io/v1",
    defaultModel: "MiniMax-M2.7",
    knownModels: ["MiniMax-M2.7", "MiniMax-M2.7-highspeed", "MiniMax-M2.5"],
    docsUrl: "https://platform.minimax.io/docs/token-plan/other-tools",
  }),
  entry("ollama", "Ollama", "openai-compatible", "direct", "openai-chat-compatible", ["chat", "image", "vision", "local"], {
    baseUrl: "http://localhost:11434/v1",
    defaultModel: "llama3.2",
    knownModels: ["llama3.2", "llama3.1", "qwen2.5", "gemma3"],
    requiresApiKey: false,
    docsUrl: "https://docs.ollama.com/api/openai-compatibility",
    imageProvider: "openai",
  }),
  entry("llama-local", "Local Llama / llama.cpp", "openai-compatible", "direct", "openai-chat-compatible", ["chat", "local"], {
    baseUrl: "http://localhost:8080/v1",
    baseUrlRequired: true,
    defaultModel: "local-model",
    requiresApiKey: false,
  }),
  entry("lm-studio", "LM Studio", "openai-compatible", "direct", "openai-chat-compatible", ["chat", "vision", "local"], {
    baseUrl: "http://localhost:1234/v1",
    defaultModel: "local-model",
    requiresApiKey: false,
  }),
  entry("openai-images", "OpenAI Images", "openai", "direct", "openai-images", ["image"], {
    defaultModel: "gpt-image-2",
    knownModels: ["gpt-image-2", "gpt-image-1.5", "gpt-image-1", "gpt-image-1-mini", "dall-e-3"],
    imageProvider: "openai",
    docsUrl: "https://developers.openai.com/api/docs/guides/image-generation",
  }),
  entry("duct-tape", "Duct Tape / GPT Image", "openai", "direct", "openai-images", ["image"], {
    defaultModel: "gpt-image-2",
    knownModels: ["gpt-image-2", "gpt-image-1.5", "gpt-image-1", "gpt-image-1-mini"],
    imageProvider: "openai",
    docsUrl: "https://developers.openai.com/api/docs/guides/image-generation",
  }),
  entry("nano-banana", "Nano Banana / Gemini Image", "google", "direct", "gemini-images", ["image", "vision"], {
    baseUrl: "https://generativelanguage.googleapis.com/v1",
    defaultModel: "gemini-3.1-flash-image",
    knownModels: ["gemini-3.1-flash-image", "gemini-3-pro-image", "gemini-2.5-flash-image"],
    imageProvider: "gemini",
    docsUrl: "https://ai.google.dev/gemini-api/docs/image-generation",
  }),
  entry("google-imagen", "Google Imagen", "google", "direct", "google-imagen", ["image"], {
    baseUrl: "https://generativelanguage.googleapis.com/v1beta",
    defaultModel: "imagen-4.0-generate-001",
    knownModels: [
      "imagen-4.0-generate-001",
      "imagen-4.0-fast-generate-001",
      "imagen-4.0-ultra-generate-001",
    ],
    imageProvider: "imagen",
    docsUrl: "https://ai.google.dev/gemini-api/docs/imagen",
  }),
  entry("image-openai-compatible", "Custom OpenAI Image API", "openai-compatible", "direct", "openai-images", ["image"], {
    baseUrlRequired: true,
    defaultModel: "gpt-image-1",
    imageProvider: "openai",
  }),
  entry("comfy", "ComfyUI", "openai-compatible", "direct", "comfy", ["image", "local"], {
    baseUrl: "http://127.0.0.1:8188",
    requiresApiKey: false,
    imageProvider: "comfy",
    docsUrl: "https://docs.comfy.org/development/comfyui-server/comms_routes",
  }),
  entry("stable-diffusion-webui", "Stable Diffusion WebUI", "openai-compatible", "direct", "stable-diffusion-webui", ["image", "local"], {
    baseUrl: "http://127.0.0.1:7860",
    requiresApiKey: false,
    imageProvider: "stable-diffusion-webui",
    docsUrl: "https://github.com/AUTOMATIC1111/stable-diffusion-webui/wiki/API",
  }),
];

export function getProviderCatalogEntry(id: string | undefined): ProviderCatalogEntry | undefined {
  return id ? PROVIDER_CATALOG.find((entry) => entry.id === id) : undefined;
}

export function providerSupportsCapability(
  profile: Pick<ProviderProfile, "capabilities" | "kind" | "providerId">,
  capability: ProviderCapability,
): boolean {
  if (profile.capabilities?.length) return profile.capabilities.includes(capability);
  const entry = getProviderCatalogEntry(profile.providerId);
  if (entry) return entry.capabilities.includes(capability);
  if (capability === "chat") return true;
  if (capability === "image") return profile.kind === "openai" || profile.kind === "google" || profile.kind === "platform";
  return false;
}

function entry(
  id: string,
  label: string,
  kind: ProviderKind,
  transport: TransportMode,
  apiFamily: ProviderApiFamily,
  capabilities: ProviderCapability[],
  init: Partial<Omit<ProviderCatalogEntry, "id" | "label" | "kind" | "transport" | "apiFamily" | "capabilities">> = {},
): ProviderCatalogEntry {
  return {
    ...init,
    id,
    label,
    kind,
    transport,
    apiFamily,
    capabilities,
    requiresApiKey: init.requiresApiKey ?? true,
  };
}
